/**
/**
 * Medical questionnaire form handler that manages form submission, photo uploads,
 * form validation, and dynamic form behavior for SkinStation medical questionnaires.
 * Handles loading/saving questionnaire data, photo gallery management, and
 * validation of required fields including medical conditions, photos and consents.
 *
 * © 2025 GoMage — All rights reserved
 */

(function () {
  const forms = document.querySelectorAll("[data-medical-questionnaire-form], #medical-form");
  if (!forms.length) return;

  forms.forEach((form) => {
    if (form.dataset.mqInitialized === "true") return;
    form.dataset.mqInitialized = "true";

    const PHOTOS_URL = form.dataset.photosUrl || "/apps/medical/questionnaire?read=1&withPhotos=1";
    const previouslyUsedProductsField = form.dataset.previouslyUsedProductsField || "previously_used_products";
    const previouslyRecommendedProductsField = form.dataset.previouslyRecommendedProductsField || "previously_recommended_products";
    const isCheckout = form.dataset.isCheckout === "true";

    const photoMsg = form.querySelector("[data-photo-msg], #photo-msg");
    const msg = form.querySelector("[data-q-msg], #q-msg");
    const loading = form.querySelector("[data-loading], #loading");
    const loadingText = form.querySelector("[data-loading-text], #loading-text");
    const submitBtn = form.querySelector("[data-submit-btn], #submit-btn");

    const MAX_MB = 5;
    const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];

    let hasAnyPhotos = false;
    let photoError = null;
    let uploadLocked = false;
    let hadPhotosOnInit = false;
    let _photosInitCaptured = false;

    const MAX_PHOTO_AGE_MS = 182 * 24 * 60 * 60 * 1000;
    let lastPhotoUploadedAt = null;

    function photoRefreshReason() {
      if (hasAnyPhotos && !lastPhotoUploadedAt) return "unknownDate";
      if (!lastPhotoUploadedAt) return null;

      return Date.now() - lastPhotoUploadedAt > MAX_PHOTO_AGE_MS ? "tooOld" : null;
    }

    function updatePhotoRequired() {
      const fileInput = form.querySelector("[data-photo-input], #f-photo");
      const photoLabel = form.querySelector(".q-photo-label");

      const needPhotoBecauseNone = !hasAnyPhotos;
      const reason = photoRefreshReason();
      const needPhotoBecauseOldOrUnknown = hasAnyPhotos && !!reason;
      const needPhoto = !uploadLocked && (needPhotoBecauseNone || needPhotoBecauseOldOrUnknown);

      if (fileInput) fileInput.required = needPhoto;
      if (photoLabel) photoLabel.classList.toggle("required", needPhoto);

      if (needPhotoBecauseOldOrUnknown && !uploadLocked) {
        const text =
          reason === "unknownDate"
            ? "We couldn't determine when your last photo was uploaded. Please upload a new up-to-date photo."
            : "It has been more than 6 months since your last photo upload. Please upload a new up-to-date photo.";

        setMsg(photoMsg, text, false);
      }
    }

    function renderPhotos(items) {
      const wrap = form.querySelector("[data-photo-gallery], #photo-gallery");
      if (!wrap) return;
      wrap.innerHTML = "";
      const list = Array.isArray(items) ? items : [];
      hasAnyPhotos = list.length > 0;

      if (!_photosInitCaptured) {
        hadPhotosOnInit = hasAnyPhotos;
        _photosInitCaptured = true;
      }

      updatePhotoRequired();
      if (list.length === 0) {
        const p = document.createElement("p");
        p.className = "empty";
        p.textContent = "No photos yet";
        wrap.appendChild(p);
        return;
      }
      for (const it of list) {
        const a = document.createElement("a");
        a.href = it.url || (it.meta && it.meta.publicUrl) || "#";
        a.target = "_blank";
        a.rel = "noopener";
        const img = document.createElement("img");
        img.alt = (it.meta && it.meta.alt) || "photo";
        img.src = (it.meta && it.meta.thumb) || it.url || (it.meta && it.meta.publicUrl);
        const ph = document.createElement("div");
        ph.className = "ph";
        a.appendChild(img);
        ph.appendChild(a);
        wrap.appendChild(ph);
      }
    }

    async function loadPhotos() {
      try {
        const r = await fetch(PHOTOS_URL, {
          method: "GET",
          headers: { Accept: "application/json", "ngrok-skip-browser-warning": "true" },
          credentials: "same-origin",
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok || !j?.ok) throw new Error(j?.error || "Failed to load photos");
        renderPhotos(j.photos || []);
      } catch (_e) {
        renderPhotos([]);
      }
    }

    function setMsg(el, text, ok) {
      if (!el) return;
      el.textContent = text || "";
      el.className = "q-msg" + (ok === true ? " ok" : ok === false ? " err" : "");
    }

    function showLoading(text) {
      if (loading) {
        loading.classList.add("show");
        loading.setAttribute("aria-hidden", "false");
      }
      if (loadingText) loadingText.textContent = text || "";
      if (submitBtn) submitBtn.disabled = true;
    }

    function hideLoading() {
      if (loading) {
        loading.classList.remove("show");
        loading.setAttribute("aria-hidden", "true");
      }
      if (loadingText) loadingText.textContent = "";
      if (submitBtn) submitBtn.disabled = false;
    }

    async function checkUploadLockFromCart() {
      try {
        const url = "/cart.js";
        const r = await fetch(url, {
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
            "Cache-Control": "no-cache",
          },
          cache: "no-store",
        });
        const cart = await r.json();
        const attrs = cart?.attributes || {};
        const hasId = typeof attrs["questionnaire_photo"] !== "undefined" && String(attrs["questionnaire_photo"]).trim() !== "";
        const hasBool = String(attrs["medical_questionnaire.upload_photo"]).toLowerCase() === "true";

        // Cart attributes can be stale leftovers from a different customer/session on the same cart,
        // so only honor the lock when it's corroborated by an actual photo on this questionnaire record.
        const lockedNow = (hasId || hasBool) && hasAnyPhotos;

        if (lockedNow && !_photosInitCaptured) {
          hadPhotosOnInit = true;
          _photosInitCaptured = true;
        }

        if (lockedNow && !uploadLocked) {
          uploadLocked = true;
          lockUploadUI("A photo is already attached to your order.");
        } else if (!lockedNow && uploadLocked) {
          uploadLocked = false;
          unlockUploadUI();
          setMsg(photoMsg, "");
        } else {
          uploadLocked = lockedNow;
          if (uploadLocked) lockUploadUI("A photo is already attached to your order.");
          else unlockUploadUI();
        }
      } catch (_) {}
    }

    window.addEventListener("pageshow", (e) => {
      if (e.persisted) checkUploadLockFromCart();
    });
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") checkUploadLockFromCart();
    });

    function lockUploadUI(message) {
      const fileInput = form.querySelector("[data-photo-input], #f-photo");
      const label = form.querySelector(".q-photo-label");
      if (fileInput) {
        fileInput.disabled = true;
        fileInput.setAttribute("aria-disabled", "true");
      }
      if (label) label.classList.remove("required");
      updatePhotoRequired();
      if (message) setMsg(photoMsg, message, true);
    }

    function unlockUploadUI() {
      const fileInput = form.querySelector("[data-photo-input], #f-photo");
      if (fileInput) {
        fileInput.disabled = false;
        fileInput.removeAttribute("aria-disabled");
      }
      updatePhotoRequired();
    }

    const mcOtherWrap = form.querySelector("[data-mc-other-wrap], #mc-other-wrap");

    const getControl = (name) => {
      const el = form?.elements?.[name];
      if (!el) return null;
      return el instanceof RadioNodeList ? el[0] : el;
    };

    function prefillSkinTypes(arr) {
      const set = new Set(Array.isArray(arr) ? arr : []);
      form.querySelectorAll('input[name="skin_type[]"]').forEach((cb) => {
        cb.checked = set.has(cb.value);
      });
    }

    function normalizeProductReference(value) {
      if (!value) return "";
      if (typeof value === "string") {
        if (/^\d+$/.test(value)) return `gid://shopify/Product/${value}`;
        return value;
      }
      if (typeof value === "object") {
        if (typeof value.id === "string" && value.id.startsWith("gid://")) return value.id;
        if (value.admin_graphql_api_id) return value.admin_graphql_api_id;
        if (value.gid) return value.gid;
        if (value.id) return `gid://shopify/Product/${value.id}`;
      }
      return "";
    }

    function prefillProductReferences(name, value, shouldCheck = true) {
      const values = Array.isArray(value) ? value : value ? [value] : [];
      const set = new Set(values.map(normalizeProductReference).filter(Boolean));
      form.querySelectorAll(`input[name="${name}[]"]`).forEach((cb) => {
        cb.checked = shouldCheck && set.has(cb.value);
      });
      return set;
    }

    // Groups listed here are expanded: as soon as one product in the group has been previously
    // used, every product in that group is offered as a repeat option.
    const EXPANDABLE_PRODUCT_GROUP_KEYS = ["tretinoin"];

    function getExpandedProductGroupKeys(usedSet) {
      const expandedKeys = new Set();
      form.querySelectorAll('[data-product-option][data-product-group="previously_used"]').forEach((option) => {
        const groupKey = option.dataset.productGroupKey || "";
        if (!groupKey || !EXPANDABLE_PRODUCT_GROUP_KEYS.includes(groupKey)) return;
        if (usedSet.has(option.dataset.productGid || "")) expandedKeys.add(groupKey);
      });
      return expandedKeys;
    }

    function updateProductReferenceVisibility(previouslyUsedSet) {
      const usedSet = previouslyUsedSet || new Set();
      const expandedGroupKeys = getExpandedProductGroupKeys(usedSet);
      let visiblePreviouslyUsedCount = 0;
      const visibleGroups = {
        previously_used: new Set(),
        previously_recommended: new Set(),
      };

      form.querySelectorAll('[data-product-option][data-product-group="previously_used"]').forEach((option) => {
        const productGid = option.dataset.productGid || "";
        const groupKey = option.dataset.productGroupKey || "";
        const show = usedSet.has(productGid) || (groupKey && expandedGroupKeys.has(groupKey));
        option.hidden = !show;
        if (show) {
          visiblePreviouslyUsedCount += 1;
          if (groupKey) visibleGroups.previously_used.add(groupKey);
        }
      });

      form.querySelectorAll('[data-product-option][data-product-group="previously_recommended"]').forEach((option) => {
        const productGid = option.dataset.productGid || "";
        const groupKey = option.dataset.productGroupKey || "";
        option.hidden = usedSet.has(productGid) || (groupKey && expandedGroupKeys.has(groupKey));
        if (!option.hidden && groupKey) {
          visibleGroups.previously_recommended.add(groupKey);
        }
      });

      form.querySelectorAll("[data-product-list-heading]").forEach((heading) => {
        const group = heading.dataset.productGroup || "";
        const key = heading.dataset.productGroupKey || "";
        heading.hidden = !visibleGroups[group]?.has(key);
      });

      form.querySelectorAll("[data-repeat-products-empty]").forEach((el) => {
        el.hidden = visiblePreviouslyUsedCount > 0;
      });
      form.querySelectorAll("[data-repeat-products-footer]").forEach((el) => {
        el.hidden = visiblePreviouslyUsedCount === 0;
      });
    }

    function prefillMedicalConditions(arr) {
      const set = new Set(Array.isArray(arr) ? arr : []);
      form.querySelectorAll('input[name="medical_condition[]"]').forEach((cb) => {
        cb.checked = set.has(cb.value);
      });
      toggleOther(set.has("OTHER"));
    }

    function prefillMedicalReasons(val) {
      let arr = [];
      if (Array.isArray(val)) arr = val;
      else if (typeof val === "string" && val.trim()) {
        try {
          const parsed = JSON.parse(val);
          arr = Array.isArray(parsed)
            ? parsed
            : String(val)
                .split(",")
                .map((s) => s.trim());
        } catch {
          arr = String(val)
            .split(",")
            .map((s) => s.trim());
        }
      }
      const set = new Set(arr.map(String));
      form.querySelectorAll('input[name="medical_reason[]"]').forEach((cb) => {
        cb.checked = set.has(cb.value);
      });
    }

    function toggleOther(show) {
      if (mcOtherWrap) mcOtherWrap.style.display = show ? "" : "none";
      const ta = form.querySelector('textarea[name="medical_condition_text"]');
      if (ta) ta.required = !!show;
      const medTextLabel = form.querySelector("[data-med-text-label], #med-text-label");
      if (medTextLabel) medTextLabel.classList.toggle("required", !!show);
      if (!show && ta) ta.value = "";
    }

    function attachMedicalNoneLogic() {
      const all = [...form.querySelectorAll('input[name="medical_condition[]"]')];
      const none = all.find((i) => i.value === "NONE");
      if (!none) return;
      all.forEach((cb) => {
        cb.addEventListener("change", () => {
          if (cb.value === "NONE" && cb.checked) {
            all.forEach((x) => {
              if (x !== none) x.checked = false;
            });
            toggleOther(false);
          } else if (cb.value !== "NONE" && cb.checked) {
            none.checked = false;
          }
          const other = all.find((x) => x.value === "OTHER");
          toggleOther(!!other && other.checked);
        });
      });
    }

    function prefillNormalPractice(val) {
      let arr = [];
      if (Array.isArray(val)) arr = val;
      else if (typeof val === "string" && val.trim()) arr = [val.trim()];
      const set = new Set(arr.map(String));
      const a = form.querySelector('input[name="normal_practice[]"][value="A"]');
      const b = form.querySelector('input[name="normal_practice[]"][value="B"]');
      if (a) a.checked = set.has("A");
      if (b) b.checked = set.has("B");
    }

    function prefillRadio(name, value) {
      const normalizedValue = String(value || "").toLowerCase();
      let valueToSelect = "";
      if (["yes", "y", "true", "1"].includes(normalizedValue)) valueToSelect = "yes";
      if (["no", "n", "false", "0"].includes(normalizedValue)) valueToSelect = "no";
      form.querySelectorAll(`input[name="${name}"]`).forEach((radio) => {
        radio.checked = radio.value === valueToSelect;
      });
    }

    function updateConditionalProductSections() {
      const repeatProductsSection = form.querySelector("[data-repeat-products-section]");
      const currentTreatmentProductsSection = form.querySelector("[data-current-treatment-products-section]");
      const prescriptionContactSection = form.querySelector("[data-prescription-contact-section]");
      const repeatProductsAnswer = form.querySelector('input[name="previously_used_products_logic"]:checked')?.value;
      const currentTreatmentAnswer = form.querySelector('input[name="previously_recommended_products_logic"]:checked')?.value;

      if (repeatProductsSection) repeatProductsSection.hidden = repeatProductsAnswer !== "yes";
      if (currentTreatmentProductsSection) currentTreatmentProductsSection.hidden = currentTreatmentAnswer !== "yes";
      if (prescriptionContactSection) {
        const showContact = repeatProductsAnswer === "no" && currentTreatmentAnswer === "no";
        const checkbox = prescriptionContactSection.querySelector('input[name="consent_to_callback"]');
        prescriptionContactSection.hidden = !showContact;
        if (checkbox) {
          checkbox.required = showContact;
          checkbox.setAttribute("aria-required", showContact ? "true" : "false");
        }
        if (!showContact) {
          if (checkbox) checkbox.checked = false;
        }
      }
    }

    function attachConditionalProductSections() {
      form.querySelectorAll('input[name="previously_used_products_logic"], input[name="previously_recommended_products_logic"]').forEach((radio) => {
        radio.addEventListener("change", updateConditionalProductSections);
      });
      updateConditionalProductSections();
    }

    function setProductsLinkVisible(isVisible) {
      const section = form.dataset.sectionId ? document.getElementById(form.dataset.sectionId) : null;
      const link = section?.querySelector("[data-questionnaire-products-link]");
      if (!link) return;
      link.hidden = !isVisible;
    }

    function setIntro(hasExisting) {
      const intro = form.querySelector("[data-q-description], #q-description");
      setProductsLinkVisible(hasExisting);
      if (intro) {
        intro.textContent = hasExisting
          ? "Please check your previous answers and make sure they are accurate"
          : "Please answer these questions accurately to help us provide the best possible care.";
      }
    }

    async function prefill() {
      try {
        const r = await fetch(PHOTOS_URL, {
          credentials: "same-origin",
          headers: { Accept: "application/json", "ngrok-skip-browser-warning": "true" },
        });
        const j = await r.json();
        setIntro(!!(r.ok && j?.ok && j.questionnaire));
        if (!r.ok || !j?.ok || !j.questionnaire) {
          attachMedicalNoneLogic();
          attachConditionalProductSections();
          renderPhotos(j?.photos || []);
          return;
        }
        const q = j.questionnaire;

        if (q.photo_last_uploaded_at) {
          const t = Date.parse(q.photo_last_uploaded_at);
          if (!Number.isNaN(t)) {
            lastPhotoUploadedAt = t;
          }
        }

        const set = (name, val) => {
          const ctrl = getControl(name);
          if (!ctrl) return;
          if (ctrl.type === "checkbox") ctrl.checked = !!val;
          else ctrl.value = val ?? "";
        };
        set("metaobject_id", q.id);
        set("contact_number", q.contact_number || "");
        set("dob", q.dob || "");
        set("medical_condition_text", q.medical_condition_text || "");
        set("current_medications", q.current_medications || "");
        set("other_health_issues", q.other_health_issues || "");
        if (Array.isArray(q.skin_type)) prefillSkinTypes(q.skin_type);
        const previouslyUsedProductsSet = prefillProductReferences(
          "previously_used_products",
          q[previouslyUsedProductsField] || q.prescribed_products || q.previously_used_products || q.have_you_previously_used_any_products_to_treat_any_concerns,
          false,
        );
        prefillProductReferences(
          "previously_recommended_products",
          q[previouslyRecommendedProductsField] || q.recommended_products || q.previously_recommended_products,
          false,
        );
        updateProductReferenceVisibility(previouslyUsedProductsSet);
        if (Array.isArray(q.medical_condition)) prefillMedicalConditions(q.medical_condition);
        if (q.normal_practice != null) prefillNormalPractice(q.normal_practice);
        if (q.medical_reason != null) prefillMedicalReasons(q.medical_reason);
        prefillRadio("previously_used_products_logic", q.previously_used_products_logic);
        prefillRadio("previously_recommended_products_logic", q.previously_recommended_products_logic);
        updateConditionalProductSections();

        [
          "confirm_age",
          "not_pregnant",
          "personal_use",
          "confirm_waive",
          "consent_to_prescribe",
          "aware_submax",
          "aware_no_wax",
          "confirm_register",
          "new_prescription_test",
        ].forEach((k) => set(k, !!q[k]));

        attachMedicalNoneLogic();
        attachConditionalProductSections();
        renderPhotos(j.photos || []);
      } catch (_e) {
        attachMedicalNoneLogic();
        attachConditionalProductSections();
        renderPhotos([]);
      }
    }

    prefill().then(() => checkUploadLockFromCart());

    function atLeastOneChecked(selector) {
      return Array.from(form.querySelectorAll(selector)).some((i) => i.checked);
    }

    function hasVisibleCheckedProduct(section, selector) {
      if (!section || section.hidden) return false;
      return Array.from(section.querySelectorAll(selector)).some((input) => {
        const option = input.closest("[data-product-option]");
        return input.checked && !input.disabled && !(option && option.hidden);
      });
    }

    function bytesToMB(bytes) {
      return (bytes / 1024 / 1024).toFixed(2);
    }

    function validateFile(file) {
      if (!file) return;
      if (file.size > MAX_MB * 1024 * 1024) {
        throw new Error(`File is too large (${bytesToMB(file.size)}MB). Maximum is ${MAX_MB}MB`);
      }
      if (!ALLOWED.includes(file.type)) {
        throw new Error("Unsupported file type");
      }
    }

    (function attachInstantPhotoValidation() {
      const fileInput = form.querySelector("[data-photo-input], #f-photo");
      if (!fileInput) return;

      fileInput.addEventListener("change", () => {
        setMsg(photoMsg, "");
        if (uploadLocked) {
          fileInput.value = "";
          setMsg(photoMsg, "A photo is already attached to your order.", false);
          return;
        }

        const f = fileInput.files?.[0];

        if (!f) {
          photoError = null;
          return;
        }
        try {
          validateFile(f);
          photoError = null;
          setMsg(photoMsg, `Selected: ${f.name} (${bytesToMB(f.size)}MB)`, true);
        } catch (e) {
          photoError = e?.message || "Invalid file";
          setMsg(photoMsg, photoError, false);
          fileInput.value = "";
        }
      });
    })();

    function validateRequired() {
      const contact = form.querySelector('input[name="contact_number"]')?.value?.trim();
      if (!contact) throw new Error("Please enter contact number");
      if (!atLeastOneChecked('input[name="skin_type[]"]')) {
        throw new Error("Please select at least one skin type");
      }
      const mcs = Array.from(form.querySelectorAll('input[name="medical_condition[]"]'))
        .filter((i) => i.checked)
        .map((i) => i.value);
      if (mcs.length === 0) throw new Error("Please select at least one medical condition");
      const needsOther = mcs.includes("OTHER");
      const medTxt = form.querySelector('textarea[name="medical_condition_text"]')?.value?.trim() || "";
      if (needsOther && !medTxt) throw new Error("Please describe other medical condition(s)");
      const meds = form.querySelector('textarea[name="current_medications"]')?.value?.trim();
      if (!meds) throw new Error("Please fill current medications");
      if (form.querySelector('input[name="previously_used_products_logic"]') && !form.querySelector('input[name="previously_used_products_logic"]:checked')) {
        throw new Error("Please answer whether you are requesting repeat medication");
      }
      if (form.querySelector('input[name="previously_recommended_products_logic"]') && !form.querySelector('input[name="previously_recommended_products_logic"]:checked')) {
        throw new Error("Please answer whether you are currently using medical skincare treatment");
      }
      const currentTreatmentAnswer = form.querySelector('input[name="previously_recommended_products_logic"]:checked')?.value;
      const currentTreatmentProductsSection = form.querySelector("[data-current-treatment-products-section]");
      if (currentTreatmentAnswer === "yes" && !hasVisibleCheckedProduct(currentTreatmentProductsSection, 'input[name="previously_recommended_products[]"]')) {
        throw new Error("Please select at least one previously recommended product");
      }
      const prescriptionContactSection = form.querySelector("[data-prescription-contact-section]");
      const callbackConsent = form.querySelector('input[name="consent_to_callback"]');
      if (prescriptionContactSection && !prescriptionContactSection.hidden && !callbackConsent?.checked) {
        throw new Error("Please confirm you're happy for us to call you to arrange a consultation");
      }
      if (!atLeastOneChecked('input[name="medical_reason[]"]')) {
        throw new Error("Please select at least one medical reason for your prescription request");
      }

      const dob = form.querySelector('input[name="dob"]')?.value?.trim();
      if (!dob) throw new Error("Please select date of birth");
      const other = form.querySelector('textarea[name="other_health_issues"]')?.value?.trim();
      if (!other) throw new Error("Please fill other health issues");
      const npA = form.querySelector('input[name="normal_practice[]"][value="A"]')?.checked;
      const npB = form.querySelector('input[name="normal_practice[]"][value="B"]')?.checked;
      if (!npA && !npB) throw new Error("Please select at least one option in Normal practice");
      const bools = [
        "confirm_age",
        "not_pregnant",
        "personal_use",
        "confirm_waive",
        "consent_to_prescribe",
        "aware_submax",
        "aware_no_wax",
        "confirm_register",
        "new_prescription_test",
      ];
      for (const k of bools) {
        const cb = form.querySelector(`input[name="${k}"]`);
        if (!cb || !cb.checked) throw new Error("Please confirm all required acknowledgements");
      }
      const file = form.querySelector("[data-photo-input], #f-photo")?.files?.[0];
      if (photoError) {
        throw new Error(photoError);
      }
      if (uploadLocked) {
        return true;
      }

      const reason = photoRefreshReason();
      const needsNewPhoto = !hasAnyPhotos || !!reason;

      if (needsNewPhoto && !file) {
        if (reason === "unknownDate") {
          throw new Error("We couldn't determine when your last photo was uploaded. Please upload a new photo.");
        }
        if (reason === "tooOld") {
          throw new Error("Your previous photo is too old. Please upload a new photo.");
        }
        throw new Error("Please upload a photo");
      }

      if (file) validateFile(file);
      return true;
    }

    function collectQuestionnaireData() {
      const fd = new FormData(form);
      const bools = [
        "confirm_age",
        "not_pregnant",
        "personal_use",
        "confirm_waive",
        "consent_to_prescribe",
        "aware_submax",
        "aware_no_wax",
        "confirm_register",
        "new_prescription_test",
      ];
      const data = Object.fromEntries(fd.entries());
      bools.forEach((k) => (data[k] = !!fd.get(k)));
      data.skin_type = fd.getAll("skin_type[]");
      const previouslyUsedProductsLogic = fd.get("previously_used_products_logic");
      const previouslyRecommendedProductsLogic = fd.get("previously_recommended_products_logic");
      const consentToCallback = !!fd.get("consent_to_callback");
      if (previouslyUsedProductsLogic) {
        data.previously_used_products_logic = previouslyUsedProductsLogic === "yes";
      }
      if (previouslyRecommendedProductsLogic) {
        data.previously_recommended_products_logic = previouslyRecommendedProductsLogic === "yes";
      }
      data.consent_to_callback = consentToCallback;
      if (consentToCallback) {
        data.prescription_contact_email_to = "kenny.foster@healthxchange.com";
        data.prescription_contact_email_requested = true;
        data.prescription_contact_email_message =
          "Please can you complete the rest of the medical consultation form to help our clinicians make any informed decision in treating your medical concern. We will call you to arrange a suitable time for consultation. Please tick here if you're happy for us to call you to arrange a consultation";
      }
      data[previouslyUsedProductsField] = JSON.stringify(fd.getAll("previously_used_products[]"));
      data[previouslyRecommendedProductsField] = JSON.stringify(fd.getAll("previously_recommended_products[]"));
      data.medical_condition = fd.getAll("medical_condition[]");
      data.normal_practice = fd.getAll("normal_practice[]");
      data.medical_reason = fd.getAll("medical_reason[]");
      return data;
    }

    async function saveQuestionnaire(data) {
      const r = await fetch("/apps/medical/questionnaire", {
        method: "POST",
        headers: { "Content-Type": "application/json", "ngrok-skip-browser-warning": "true" },
        credentials: "same-origin",
        body: JSON.stringify(data),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || "Save error");
      return j;
    }

    async function uploadPhoto(ensureMetaobjectOnce) {
      const fileInput = form.querySelector("[data-photo-input], #f-photo");
      const file = fileInput?.files?.[0];
      if (!file) return { uploaded: false };

      if (uploadLocked) {
        throw new Error("A photo is already attached to your order.");
      }

      validateFile(file);
      photoError = null;

      const doUpload = async () => {
        const fd = new FormData();
        fd.append("photo", file);
        fd.append("customer_id", form.elements["customer_id"].value);
        const r = await fetch("/apps/medical/questionnaire/photo", {
          method: "POST",
          body: fd,
          credentials: "same-origin",
        });
        const j = await r.json().catch(() => ({}));
        return { ok: r.ok, status: r.status, body: j };
      };

      let res = await doUpload();

      if (!res.ok && (res.status === 409 || res?.body?.stage === "metaobjectMissing") && typeof ensureMetaobjectOnce === "function") {
        await ensureMetaobjectOnce();
        await new Promise((r) => setTimeout(r, 400));
        res = await doUpload();
      }

      if (!res.ok) throw new Error(res?.body?.error || "Photo upload failed");
      if (fileInput) fileInput.value = "";
      setMsg(photoMsg, "Photo uploaded", true);
      lastPhotoUploadedAt = Date.now();

      await loadPhotos();
      hasAnyPhotos = true;
      updatePhotoRequired();

      uploadLocked = true;
      lockUploadUI("A photo is already attached to your order.");

      return { uploaded: true, fileId: res.body?.added };
    }

    async function updateCartAttributes(attrs) {
      const params = new URLSearchParams();
      Object.entries(attrs).forEach(([k, v]) => {
        params.append(`attributes[${k}]`, String(v));
      });

      const resp = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          Accept: "application/json",
        },
        body: params.toString(),
        credentials: "same-origin",
        cache: "no-store",
      });

      try {
        await resp.json();
      } catch {}

      if (!resp.ok) throw new Error("Cart update failed");
    }

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      setMsg(msg, "");
      setMsg(photoMsg, "");

      try {
        validateRequired();

        const data = collectQuestionnaireData();

        showLoading("Saving form…");
        await saveQuestionnaire(data);
        setMsg(msg, "Form saved", true);

        showLoading("Uploading photo…");
        const uploaded = await uploadPhoto(async () => {
          await saveQuestionnaire(data);
        });

        try {
          showLoading("Updating cart…");

          const attrs = { questionnaire_submitted: "true" };
          if (uploaded?.uploaded) {
            attrs["medical_questionnaire.upload_photo"] = "true";
            attrs["questionnaire_photo"] = String(uploaded.fileId || "true");
          } else if (hadPhotosOnInit) {
            attrs["medical_questionnaire.photo_preexisting"] = "true";
          }

          await updateCartAttributes(attrs);
          setMsg(msg, "");
        } catch (e2) {
          setMsg(msg, e2?.message || "Failed to update cart", false);
          return;
        } finally {
          hideLoading();
        }

        if (isCheckout) {
          const ret = form.elements["return_to"]?.value;
          window.location.href = ret ? decodeURIComponent(ret) : "/checkout";
          return;
        }

        setMsg(msg, "Thank you for submitting your online consultation. Our clinical team will review this and be in touch soon.", true);
        setProductsLinkVisible(true);
      } catch (err) {
        hideLoading();
        setMsg(msg, err?.message || "Error", false);
      }
    });
  });
})();
